import type { Permission, SectionPermission } from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export type RouteAccess = 'public' | SectionPermission;

export interface RouteDefinition {
  readonly pattern: string;
  readonly access: RouteAccess;
  readonly titleKey: TranslationKey;
  readonly helpKey: TranslationKey;
}

export interface NavigationItem {
  readonly path: string;
  readonly permission: SectionPermission;
  readonly labelKey: TranslationKey;
}

/** Organization segment used before `/me` has named the active organization. */
export const placeholderOrganizationId = 'current';

export const routes = [
  { pattern: '/login', access: 'public', titleKey: 'page.login', helpKey: 'help.login' },
  { pattern: '/invite', access: 'public', titleKey: 'page.invite', helpKey: 'help.invite' },
  {
    pattern: '/app/:organizationId/home',
    access: 'overview.view',
    titleKey: 'nav.overview',
    helpKey: 'help.overview',
  },
  {
    pattern: '/athletes',
    access: 'athletes.view',
    titleKey: 'nav.athletes',
    helpKey: 'help.athletes',
  },
  {
    pattern: '/athletes/:athleteId',
    access: 'athletes.view',
    titleKey: 'nav.athletes',
    helpKey: 'help.athletes',
  },
  { pattern: '/teams', access: 'teams.view', titleKey: 'nav.teams', helpKey: 'help.teams' },
  {
    pattern: '/teams/:teamId',
    access: 'teams.view',
    titleKey: 'nav.teams',
    helpKey: 'help.teams',
  },
  {
    pattern: '/calendar',
    access: 'calendar.view',
    titleKey: 'nav.calendar',
    helpKey: 'help.calendar',
  },
  {
    pattern: '/events/:eventId',
    access: 'calendar.view',
    titleKey: 'nav.calendar',
    helpKey: 'help.calendar',
  },
  {
    pattern: '/training',
    access: 'training.view',
    titleKey: 'nav.training',
    helpKey: 'help.training',
  },
  {
    pattern: '/assessments',
    access: 'assessments.view',
    titleKey: 'nav.assessments',
    helpKey: 'help.assessments',
  },
  {
    pattern: '/assessments/:assessmentId',
    access: 'assessments.view',
    titleKey: 'nav.assessments',
    helpKey: 'help.assessments',
  },
  {
    pattern: '/billing',
    access: 'billing.view',
    titleKey: 'nav.billing',
    helpKey: 'help.billing',
  },
  {
    pattern: '/billing/accounts/:accountId',
    access: 'billing.view',
    titleKey: 'nav.billing',
    helpKey: 'help.billing',
  },
  {
    pattern: '/payments',
    access: 'payments.view',
    titleKey: 'nav.payments',
    helpKey: 'help.payments',
  },
  {
    pattern: '/competitions',
    access: 'competitions.view',
    titleKey: 'nav.competitions',
    helpKey: 'help.competitions',
  },
  {
    pattern: '/competitions/:competitionId',
    access: 'competitions.view',
    titleKey: 'nav.competitions',
    helpKey: 'help.competitions',
  },
  {
    pattern: '/reports',
    access: 'reports.view',
    titleKey: 'nav.reports',
    helpKey: 'help.reports',
  },
  {
    pattern: '/settings',
    access: 'settings.view',
    titleKey: 'nav.settings',
    helpKey: 'help.settings',
  },
] as const satisfies readonly RouteDefinition[];

export const navigationItems = [
  {
    path: `/app/${placeholderOrganizationId}/home`,
    permission: 'overview.view',
    labelKey: 'nav.overview',
  },
  { path: '/athletes', permission: 'athletes.view', labelKey: 'nav.athletes' },
  { path: '/teams', permission: 'teams.view', labelKey: 'nav.teams' },
  { path: '/calendar', permission: 'calendar.view', labelKey: 'nav.calendar' },
  { path: '/training', permission: 'training.view', labelKey: 'nav.training' },
  { path: '/assessments', permission: 'assessments.view', labelKey: 'nav.assessments' },
  { path: '/billing', permission: 'billing.view', labelKey: 'nav.billing' },
  { path: '/payments', permission: 'payments.view', labelKey: 'nav.payments' },
  { path: '/competitions', permission: 'competitions.view', labelKey: 'nav.competitions' },
  { path: '/reports', permission: 'reports.view', labelKey: 'nav.reports' },
  { path: '/settings', permission: 'settings.view', labelKey: 'nav.settings' },
] as const satisfies readonly NavigationItem[];

/**
 * Section routes that render a working page, with the action permissions that unlock it: `null`
 * means the page works for everyone who may open the route, a list means holding any one of them.
 * A section route missing here, or whose permissions are not held, still shows the "not enabled yet"
 * notice; keep this in step with the panels `Shell` renders.
 */
const featureAccess: Readonly<Record<string, readonly Permission[] | null>> = {
  '/app/:organizationId/home': null,
  '/athletes': null,
  '/teams': null,
  '/calendar': null,
  '/events/:eventId': null,
  '/training': null,
  '/assessments': null,
  '/assessments/:assessmentId': null,
  '/billing': ['billing.manage'],
  '/billing/accounts/:accountId': ['billing.manage'],
  '/payments': null,
  '/competitions': null,
  '/settings': ['roles.manage', 'organization.configure', 'invitations.manage'],
};

/** Whether the route has a working page for someone holding `permissions`. */
export function hasFeature(pattern: string, permissions: readonly Permission[]): boolean {
  const required = featureAccess[pattern];
  if (required === undefined) return false;
  return required === null || required.some((permission) => permissions.includes(permission));
}

export function isAccessAllowed(
  access: RouteAccess,
  grantedPermissions: ReadonlySet<SectionPermission>,
): boolean {
  return access === 'public' || grantedPermissions.has(access);
}

/** Navigation the person may use; the overview entry points at the active organization. */
export function visibleNavigation(
  grantedPermissions: ReadonlySet<SectionPermission>,
  organizationId: string = placeholderOrganizationId,
): readonly NavigationItem[] {
  return navigationItems
    .filter((item) => grantedPermissions.has(item.permission))
    .map((item) =>
      item.permission === 'overview.view' ? { ...item, path: `/app/${organizationId}/home` } : item,
    );
}
